import { useCallback, useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Check, X } from "lucide-react";
import api from "../lib/api";
import { extractError } from "../lib/formatError";

const STATUSES = ["pending", "paid", "rejected"];

export default function AdminWithdrawals() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("pending");
  const [modal, setModal] = useState(null); // {kind:"approve"|"reject", w}
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (status) params.set("status", status);
      const { data } = await api.get(`/admin/withdrawals?${params}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-withdrawals">
      <Toaster theme="dark" position="top-center" />
      <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)] mb-2">Withdrawals</div>
      <h1 className="font-display italic text-4xl text-[color:var(--ink)] mb-6">Payout queue ({total})</h1>

      <div className="flex gap-2 mb-5">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-[10px] tracking-widest uppercase border ${status === s ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
            data-testid={`tab-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {error && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{error}</div>}
      {loading ? <div className="text-[color:var(--muted)] text-sm">Loading…</div>
        : items.length === 0 ? <div className="text-[color:var(--muted)] text-sm italic">No withdrawals in this state.</div>
        : (
          <div className="border border-[color:var(--line)] overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-[color:var(--line)] text-[10px] tracking-[0.25em] uppercase text-[color:var(--muted)]">
                <tr>
                  <th className="text-left px-3 py-2 font-normal">Created</th>
                  <th className="text-left px-3 py-2 font-normal">User</th>
                  <th className="text-left px-3 py-2 font-normal">Amount</th>
                  <th className="text-left px-3 py-2 font-normal">Currency</th>
                  <th className="text-left px-3 py-2 font-normal">Address</th>
                  <th className="text-left px-3 py-2 font-normal">Status</th>
                  <th className="text-left px-3 py-2 font-normal">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--line)]">
                {items.map((w) => (
                  <tr key={w.id} data-testid={`withdrawal-${w.id}`}>
                    <td className="px-3 py-2 text-[color:var(--muted)]">{new Date(w.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-[color:var(--ink)]">{w.user_email || w.user_id.slice(0, 8)}</td>
                    <td className="px-3 py-2 font-mono text-[color:var(--gold)]">${w.amount_usd.toFixed(2)}</td>
                    <td className="px-3 py-2 uppercase tracking-widest">{w.crypto_currency}</td>
                    <td className="px-3 py-2 font-mono text-[10px] break-all max-w-[180px]">{w.crypto_address}</td>
                    <td className="px-3 py-2"><StatusPill s={w.status} /></td>
                    <td className="px-3 py-2">
                      {w.status === "pending" ? (
                        <div className="flex gap-1">
                          <button onClick={() => setModal({ kind: "approve", w })} className="px-2 py-1 border border-[color:var(--gold)] text-[color:var(--gold)] text-[10px] tracking-widest uppercase hover:bg-[color:var(--card)]" data-testid={`approve-${w.id}`}>
                            <Check className="w-3 h-3 inline" /> Pay
                          </button>
                          <button onClick={() => setModal({ kind: "reject", w })} className="px-2 py-1 border border-[color:var(--red)] text-[color:var(--red)] text-[10px] tracking-widest uppercase hover:bg-[color:var(--card)]" data-testid={`reject-${w.id}`}>
                            <X className="w-3 h-3 inline" /> Reject
                          </button>
                        </div>
                      ) : (
                        <div className="text-[10px] text-[color:var(--muted)]">
                          {w.tx_hash && <div className="font-mono break-all">tx: {w.tx_hash.slice(0, 12)}…</div>}
                          {w.reject_reason && <div className="text-[color:var(--red)]">{w.reject_reason}</div>}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {modal && (
        <ActionModal
          w={modal.w}
          kind={modal.kind}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); toast.success(`Withdrawal ${modal.kind === "approve" ? "paid" : "rejected"}`); }}
        />
      )}
    </div>
  );
}

function ActionModal({ w, kind, onClose, onDone }) {
  const [tx, setTx] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (kind === "approve") {
        await api.post(`/admin/withdrawals/${w.id}/approve`, { tx_hash: tx.trim(), admin_note: note || null });
      } else {
        await api.post(`/admin/withdrawals/${w.id}/reject`, { reason: reason.trim() });
      }
      onDone();
    } catch (e2) {
      setErr(extractError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(6,4,3,0.85)", backdropFilter: "blur(4px)" }} data-testid="withdrawal-action-modal">
      <form onSubmit={submit} className="w-full max-w-md border border-[color:var(--line)] p-6" style={{ background: "var(--card)" }}>
        <div className="flex justify-between items-center mb-4">
          <div className="font-display italic text-2xl text-[color:var(--gold)]">
            {kind === "approve" ? "Mark as paid" : "Reject withdrawal"}
          </div>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-[color:var(--muted)]" /></button>
        </div>
        <div className="text-xs text-[color:var(--muted)] mb-4">
          {w.user_email} · ${w.amount_usd.toFixed(2)} · {w.crypto_currency}
        </div>

        {kind === "approve" ? (
          <>
            <label className="block mb-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)] mb-1">Blockchain tx hash *</div>
              <input value={tx} onChange={(e) => setTx(e.target.value)} required className="bet-input font-mono text-xs" data-testid="tx-hash-input" />
            </label>
            <label className="block mb-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)] mb-1">Admin note (optional)</div>
              <input value={note} onChange={(e) => setNote(e.target.value)} className="bet-input text-xs" data-testid="admin-note-input" />
            </label>
          </>
        ) : (
          <label className="block mb-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)] mb-1">Reason (visible to user) *</div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} required rows={3} className="bet-input text-xs" data-testid="reject-reason-input" />
            <div className="text-[9px] tracking-widest text-[color:var(--muted)] mt-1">Funds will be refunded to the user's real balance.</div>
          </label>
        )}

        {err && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{err}</div>}
        <button type="submit" disabled={busy} className="spin-btn w-full" data-testid="modal-submit-btn">
          {busy ? "Working…" : kind === "approve" ? "Confirm payment" : "Confirm rejection"}
        </button>
      </form>
    </div>
  );
}

function StatusPill({ s }) {
  const map = { pending: "muted", paid: "gold", rejected: "red", approved: "gold" };
  const c = map[s] || "muted";
  return <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 border" style={{ color: `var(--${c})`, borderColor: `var(--${c})` }}>{s}</span>;
}
